/**
 * The routing primitive: a road path through points the caller supplies.
 *
 * Separate from `/trips/...` on purpose. The arrange step draws the day the user is *currently* laying
 * out, and that day has no trip yet — the trip is written when they leave the step — and no plan, which
 * only exists after generating. So this endpoint takes points and answers with a road, and it stores
 * nothing: the caller already knows what it asked for.
 *
 * What it is *not* is a second way to compute a plan. The engine's day (`packages/core/src/plan.ts`,
 * `assembleDay`) decides which points a day has — the anchors, the midday return, the slot order — and
 * the client calls this with that list already assembled, using the same function. So the picture in the
 * arrange step and the plan generated afterwards cannot disagree about the road, because they asked the
 * same question with the same points.
 */

import { z } from "zod";

import { GeoPoint } from "./common.ts";
import { RouteLeg, TransportMode } from "./trips.ts";

/**
 * How many points one call may carry.
 *
 * A bound rather than a preference: this endpoint reaches a free routing service on the caller's behalf,
 * and an unbounded list would make it an amplifier — one signed-in request for an arbitrary number of
 * upstream legs. Twenty-five is above any day the product can produce (a packed day is a handful of
 * stops plus its two anchors), so the limit is not a product rule that will surprise anyone; it is the
 * ceiling that keeps the endpoint from being a proxy.
 */
export const ROUTE_POINT_LIMIT = 25;

export const RoutePreviewRequest = z.object({
  /**
   * The stops to drive through, in the caller's own order.
   *
   * At least two: a road needs somewhere to go. The order is what the caller stated and nothing else —
   * whether the provider is allowed to improve on it is `fixed`, not this.
   */
  points: z.array(GeoPoint).min(2).max(ROUTE_POINT_LIMIT)
    .describe("Two to twenty-five stops, in the order to drive them."),
  /**
   * Whether the order is the answer or merely the input.
   *
   * `false` lets the provider find the shortest order through the same points, and that is the *suggestion*
   * this screen offers: the arrange step prices the day both ways, and taking the shortest one writes it into
   * the arrangement — after which it is the user's own order. `true` is for a day whose order is already the
   * answer: a day the user timed (`sáng` before `chiều`), and any day whose arrangement is being driven as
   * laid out. A generated plan asks for `true` on every arranged day, so the order it was given is the order
   * it drives.
   */
  fixed: z.boolean().default(false)
    .describe("Route the points in the order given rather than the shortest order."),
  /**
   * How the party moves, so the road is the road they take.
   *
   * A bicycle is not allowed on a motorway and a car is not allowed up a footpath, so the same two
   * coordinates are two different journeys depending on the answer. Carried on the request because the
   * arrange step runs *before* the trip exists — there is nowhere to read it from — and the same mode is
   * what a plan's own day is routed with, server-side, from the stored trip.
   *
   * The mode is the product's own union rather than a routing profile name: which profile a service
   * exposes, and what it is called, is the adapter's business, and a client has no way to know it.
   */
  transport: TransportMode.default("car")
    .describe("How the party moves, which decides the road profile the day is priced on."),
}).meta({
  id: "RoutePreviewRequest",
  description: "Points to drive through, and whether their order is a constraint.",
});

export const RoutePreviewResponse = z.object({
  /**
   * The order the points are driven in, as indices into the request's own array.
   *
   * Returned rather than assumed because `fixed: false` lets the provider permute: a client that numbered
   * its pins by the order it sent them would draw a line that visits pin 2 before pin 1, and the numbers
   * are the only thing tying the map to the list beside it.
   */
  order: z.array(z.int().gte(0)),
  /**
   * The day's road path as an encoded polyline (precision 5, what OSRM serves), or null when routing was
   * unavailable.
   *
   * Null is a real answer and the client has a state for it: the map draws the pins, says the distances
   * are unknown, and does not connect them. Joining the stops with straight lines is not a fallback —
   * measured against real OSRM output, road distance is 2.24× the straight line, so a drawn straight
   * line asserts a journey the user never drives.
   */
  geometry: z.string().nullable(),
  geometry_format: z.enum(["polyline5", "geojson"]).nullable(),
  /** Total road distance, or null when it was not measured. Never estimated. */
  distance_m: z.int().gte(0).nullable(),
  /** Total driving time in whole minutes, or null. */
  driving_min: z.int().gte(0).nullable(),
  /**
   * Each leg of the day the provider priced, in the order it is driven — so a screen can say what the
   * drive between one stop and the next costs rather than only what the day costs together.
   *
   * One fewer than `points` when the provider answered, and empty when it did not, which is
   * `distances_known: false` seen leg by leg. The pair each leg joins is positional: leg `k` connects
   * the points at `order[k]` and `order[k + 1]`, the same permutation the pins are numbered from.
   * `from_place_id` and `to_place_id` are null, and not because the answer is partial — the caller sent
   * coordinates, not places, so there is no place to name.
   *
   * **Each leg carries its own path**, stitched from the provider's step geometries — the reversal
   * `RouteLeg` explains at its own definition, with the measured cost that bought it. That is what lets
   * the arrange map paint each drive in a different colour instead of stroking one day-long line. A leg
   * with no steps carries no path rather than a straight line; the map skips it and still draws its pins.
   *
   * The day's own `geometry` above is these legs joined end to end, so a caller that draws one line and
   * a caller that draws the legs cannot disagree.
   *
   * The distances sum exactly to `distance_m`: both are the provider's own leg numbers, summed in the one
   * place that reports them, rather than two roundings of the same answer.
   */
  legs: z.array(RouteLeg),
  /**
   * Whether the provider answered.
   *
   * One flag rather than three nulls to inspect: the client's three responses — hide the distance, state
   * "chưa xác định", draw no line — are all the same fact, and deriving it from a null each time is how
   * the three would drift apart.
   */
  distances_known: z.boolean(),
  routing_status: z.enum(["ok", "unavailable", "quota_exceeded"]),
  /**
   * The routing profile these numbers were asked of the provider with.
   *
   * Returned so the answer can be audited rather than assumed: the request carries a transport mode
   * (`car`, `bicycle`, …), the adapter maps it to whatever profile its service exposes, and a caller that
   * is told `walking` knows a walking trip was priced as one. Measured 2026-09-29: the OSRM deployment this
   * repo talks to answers every profile with the same road, so a walking trip's numbers there are car
   * numbers — the field is what makes that visible instead of invisible.
   *
   * Present even when the provider failed: the profile is what was asked for, and that fact does not depend
   * on the answer.
   */
  profile: z.string().describe("The routing profile the day was priced on."),
}).meta({
  id: "RoutePreviewResponse",
  description: "A road path through the points asked for, with the order it was driven in.",
});

export type RoutePreviewRequest = z.infer<typeof RoutePreviewRequest>;
export type RoutePreviewResponse = z.infer<typeof RoutePreviewResponse>;
